import { MobXProviderContext } from "mobx-react";
import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { SplitButton } from "primereact/splitbutton";
import { Tag } from "primereact/tag";
import { Toast } from 'primereact/toast';
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { APP_DOCUMENT_FILE_SIZE_IN_BYTES, getErrorTitle, getToastMessageForSubscriptionLimitReached, IMAGE_TYPE_EXTENSIONS } from "../../../constants/constants";
import { USER_ROLE_TYPE } from "../../../constants/enum";
import { RouterAddress } from "../../../constants/RouterAddress";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { convertFileToDocument, Document, ReferenceType, SharedFolder } from "../../../models/document";
import { DropDownOption } from "../../../models/primereact";
import { convertEpochToDateString, convertIsoToDateOnly } from "../../../utils/stringUtils";
import { normalizePlainText, openUrlInNewTab } from "../../../utils/securityUtils";
import SubscriptionRequired from "../../../components/general/subscriptionRequiredMessage";
import Billing from "../../../features/billing/pages/Billing";
import BillingAlert from "../../../components/general/billingAlert";
import SubscribeForUnlimitedAccessDialog from "../../../components/general/SubscribeForUnlimitedAccessDialog";
import {
  appAccentPanelStyle,
  appConfirmDialogClasses,
  appDataTableWrapperStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appInnerCardStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  appWideDialogStyle,
  appSectionCardStyle,
  renderConfirmMessage,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const DocumentPage = () => {
  const { homeId, residentId, userId, caregiverId } = useParams();
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;
  const location = useLocation();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const [loading, setLoading] = useState<boolean>(false);
  const [allDoc, setAllDoc] = useState<Document[] | null>(null);
  const [selectedFile, setSelectedFile] = useState<File[] | any>(null);
  const [flag, setFlag] = useState<boolean>(false);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const fileUploadRef = useRef<FileUpload>(null);
  const toast = useRef<Toast>(null);

  const [selectedFolderForUpload, setSelectedFolderForUpload] = useState<string>("");
  const [selectedFolderForDocTable, setSelectedFolderForDocTable] = useState<string>("");
  const [selectedFolderForShare, setSelectedFolderForShare] = useState<string>("");
  const [showNewFolderInput, setShowNewFolderInput] = useState<boolean>(false);

  const [allFolders, setAllFolders] = useState<any[]>([]);
  const [folderOptions, setFolderOptions] = useState<string[]>([]);
  const [newFolderName, setNewFolderName] = useState<string>("");
  const [newFolderFlag, setNewFolderFlag] = useState<boolean>(false);

  const [showShareUserSection, setShowShareUserSection] = useState<boolean>(false);
  const [shareTargetUserEmailId, setShareTargetUserEmailId] = useState<string>("");
  const [folderSharedUsersList, setFolderSharedUsersList] = useState<SharedFolder[] | null>(null);
  const [showFolderSharedUsersList, setShowFolderSharedUsersList] = useState<boolean>(false);

  const [sharedFoldersList, setSharedFoldersList] = useState<SharedFolder[] | null>(null);
  const [sharedFoldersNameList, setSharedFoldersNameList] = useState<DropDownOption[]>([]);
  const [sharedFolderDocuments, setSharedFolderDocuments] = useState<Document[] | null>(null);
  const [sharedFolderOwner, setSharedFolderOwner] = useState<string>("");
  const [sharedFolderExpiration, setSharedFolderExpiration] = useState<string>("");
  const [selectedSharedFolder, setSelectedSharedFolder] = useState<string>("");

  const [showAddNewDocuments, setShowAddNewDocuments] = useState<boolean>(false);
  const [showDocumentLimitDialog, setShowDocumentLimitDialog] = useState<boolean>(false);

  const [showHelpTextDialog, setShowHelpTextDialog] = useState<boolean>(false);
  const [helpTextMessage, setHelpTextMessage] = useState<string>("");
  const [referenceId, setReferenceId] = useState<string>("");
  const [referenceType, setReferenceType] = useState<string>("");

  const [docViewerNonImageUrl, setDocViewerNonImageUrl] = useState<string>("");
  const [docViewerImageUrl, setDocViewerImageUrl] = useState<string>("");
  const [showDocViewer, setShowDocViewer] = useState<boolean>(false);

  const userRoleType = authStore.getUserRoleType();
  const providerWorkspaceLabel = authExperience.providerHome.profileSectionTitle.toLowerCase();
  const homeDocumentsLabel = authExperience.providerHome.homeNameLabel.replace(" Name", "");

  const [filters, setFilters] = useState({
    'folderName': { value: null, matchMode: FilterMatchMode.CONTAINS },
    'documentTitle': { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  useEffect(() => {
    const fetchFolders = async () => {
      try {
        await backendAPIStore.init();

        const { referenceId, referenceType } = getReference(homeId, residentId, userId, caregiverId);
        setReferenceId(referenceId);
        setReferenceType(referenceType);
        const folders = await backendAPIStore.listAllFoldersByReference(referenceId, referenceType);
        setAllFolders(folders);
        setSelectedFolderForUpload(folders.length > 0 ? folders[0].folderName : "");
        setSelectedFolderForDocTable(folders.length > 0 ? folders[0].folderName : "");
        setSelectedFolderForShare(folders.length > 0 ? folders[0].folderName : "");
        setFolderOptions(folders.map(folder => folder.folderName));

        const folderId = folders[0].folderId;
        const sharedFolders = await backendAPIStore.listAllSharedUsersOfFolder(folderId!, referenceId, referenceType);
        setFolderSharedUsersList(sharedFolders);

        const response = await backendAPIStore.listAllDocument(referenceId, referenceType);
        setAllDoc(response);

        const sharedFoldersWithMe = await backendAPIStore.listAllSharedFoldersWithMe();
        setSharedFoldersList(sharedFoldersWithMe);
        const sharedFoldersNameListOptions: DropDownOption[] = sharedFoldersWithMe.map(folder => {
          return {
            name: folder.folderName!,
            code: folder.folderId!
          }
        });

        setSharedFoldersNameList(sharedFoldersNameListOptions);
        if (sharedFoldersWithMe.length > 0) {
          const firstFolderId = sharedFoldersWithMe[0].folderId!;
          const documents = await backendAPIStore.listAllDocumentsForSharedFolder(firstFolderId);
          setSharedFolderDocuments(documents);
          setSharedFolderOwner(sharedFoldersWithMe[0].folderOwner);
          setSharedFolderExpiration(convertEpochToDateString(sharedFoldersWithMe[0].ttl!));
          setSelectedSharedFolder(sharedFoldersWithMe[0].folderName);
        }
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      }
    };
    fetchFolders();
  }, [newFolderFlag, flag, showShareUserSection]);

  const loadSharedUsersForFolder = async (folderName) => {
    try {
      const folderId = allFolders.find(folder => folder.folderName === folderName)?.folderId!
      const sharedUsers = await backendAPIStore.listAllSharedUsersOfFolder(folderId, referenceId, referenceType);
      setFolderSharedUsersList(sharedUsers);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
    }
  };

  const onSharedFolderFilterChange = async (e) => {
    const selectedFolderName = e.value.name;
    const folderId = sharedFoldersList!.find(folder => folder.folderId === e.value.code)?.folderId;
    const sharedFolderOwner = sharedFoldersList!.find(folder => folder.folderId === e.value.code)?.folderOwner;
    setSharedFolderOwner(sharedFolderOwner!);
    setSelectedSharedFolder(selectedFolderName);
    const sharedFolderExpiration = sharedFoldersList!.find(folder => folder.folderId === e.value.code)?.ttl;
    setSharedFolderExpiration(convertEpochToDateString(sharedFolderExpiration!));
    if (folderId) {
      try {
        await backendAPIStore.init();
        const documents = await backendAPIStore.listAllDocumentsForSharedFolder(folderId);
        setSharedFolderDocuments(documents);
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      }
    }
  };

  const handleDeleteSharedFolder = (sharedFolder: SharedFolder) => {
    confirmDialog({
      message: renderConfirmMessage(
        "Remove shared access",
        `This will remove "${sharedFolder.sharedUserId}" from the selected folder's access list.`,
      ),
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.deleteSharedFolder(sharedFolder.folderId!, sharedFolder.id!);
          setNewFolderFlag(!newFolderFlag);
        } catch (error: any) {
          toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
        } finally {
          setShowFolderSharedUsersList(!showFolderSharedUsersList);
        }
      },
      reject: () => {
        // Handle rejection if needed
      }
    });
  };

  const onSaveNewFolder = async () => {
    try {
      if (!newFolderName.trim()) {
        toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Folder name cannot be empty.', life: 3000 });
        return;
      }
      const { referenceId } = getReference(homeId, residentId, userId, caregiverId);

      const sanitizedFolderName = normalizePlainText(newFolderName, { maxLength: 120 });
      const newFolderInputRequest = {
        referenceId: referenceId,
        folderName: sanitizedFolderName,
      };
      await backendAPIStore.addNewFolder(newFolderInputRequest);
      setShowNewFolderInput(false);
      setNewFolderName("");
      setNewFolderFlag(!newFolderFlag);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
    }
  };

  const onSaveShareFolder = async () => {
    if (!shareTargetUserEmailId.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please provide a user email to share with.', life: 3000 });
      return;
    }

    const request = {
      folderId: allFolders.find(folder => folder.folderName === selectedFolderForShare)?.folderId!,
      sharedUserId: normalizePlainText(shareTargetUserEmailId, { maxLength: 160 }).toLowerCase(),
      referenceId: getReference(homeId, residentId, userId, caregiverId).referenceId,
    };

    try {
      await backendAPIStore.shareFolderWithUser(request);
      setShowShareUserSection(false);
      setShareTargetUserEmailId("");
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Folder shared successfully and notification email sent to the user.', life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
    }
  };

  const onDeleteFolder = async () => {
    if (!selectedFolderForShare) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please select a folder to delete.', life: 3000 });
      return;
    }
    confirmDialog({
      message: renderConfirmMessage(
        "Delete folder",
        `Delete the folder "${selectedFolderForShare}". Documents will remain in storage, but the folder entry will be removed.`,
      ),
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          setLoading(true);
          const { referenceId } = getReference(homeId, residentId, userId, caregiverId);
          const selectedFolderId = allFolders.find(folder => folder.folderName === selectedFolderForShare)?.folderId;
          await backendAPIStore.deleteFolder(selectedFolderId!, referenceId);
          setNewFolderFlag(!newFolderFlag);
        } catch (error: any) {
          toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
        } finally {
          setLoading(false);
        }
      },
      reject: () => {
        // Handle rejection if needed
      }
    });
  };

  const handleFilesUpload = async (
    event: any,
  ) => {

    if (!selectedFolderForUpload) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please select a folder before uploading.', life: 3000 });
      return;
    }

    if (event.files && event.files.length > 0) {
      const files = Array.from(event.files);
      setSelectedFile(files);
      if (files.length > 0) {
        setUploadLoading(true);
        await Promise.all(files.map((file) => processDocument(file)));
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
          setShowAddNewDocuments(false);
        }
        setUploadLoading(false);
        setFlag(!flag);
        setSelectedFile(null);
      }
    }
  };

  const getReference = (homeId: string | undefined, residentId: string | undefined, userId: string | undefined, caregiverId: string | undefined) => {
    let referenceId = "";
    let referenceType = "";
    if (homeId && !residentId && !caregiverId) {
      referenceId = homeId || "";
      referenceType = ReferenceType.BUSINESS_HOME;
    } else if (homeId && residentId) {
      referenceId = residentId || "";
      referenceType = ReferenceType.RESIDENT;
    } else if (homeId && caregiverId) {
      referenceId = caregiverId || "";
      referenceType = ReferenceType.CAREGIVER;
    } else {
      referenceId = userId || "";
      referenceType = ReferenceType.USER;
    }
    return { referenceId, referenceType };
  }

  const processDocument = async (file: File | any) => {
    try {
      let { referenceId, referenceType } = getReference(homeId, residentId, userId, caregiverId);

      const document = convertFileToDocument(
        file,
        referenceId,
        referenceType,
        selectedFolderForUpload,
        allFolders.find(folder => folder.folderName === selectedFolderForUpload)?.folderId!,
      );

      await backendAPIStore.addDocument(document);
      try {
        let uploadUrl = await backendAPIStore.getPresignedUrlForUpload(
          referenceId,
          referenceType,
          document.s3Key,
        );
        await backendAPIStore.uploadFileToS3(uploadUrl, file);
      } catch (error: any) {
        console.error("Error uploading document: ", error);
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'] });

        // Rollback document metadata entry
        await backendAPIStore.deleteDocument(document);
      }
    } catch (error: any) {
      console.error("Error processing document: ", error);
      if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        setShowDocumentLimitDialog(true);
      } else {
        toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
      }
    }
  };

  const handleDelete = async (doc: Document) => {
    confirmDialog({
      message: renderConfirmMessage(
        "Delete document",
        "This will permanently remove the selected document from this record.",
      ),
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          setLoading(true);
          await backendAPIStore.deleteDocument(doc);
          setFlag(!flag);
        } catch (error: any) {
          toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
        } finally {
          setLoading(false);
        }
      },
      reject: () => {
        // Do nothing on reject
      }
    });
  };

  const handleDownload = async (doc: Document) => {
    let url;
    const { referenceId, referenceType } = getReference(homeId, residentId, userId, caregiverId);

    url = await backendAPIStore.getPresignedUrlForDownload(
      referenceId,
      referenceType,
      doc.documentId,
      doc.s3Key,
      doc.documentTitle
    );
    if (url) openUrlInNewTab(url);
  };

  const previewDocument = async (doc: Document) => {
    let url;
    const { referenceId, referenceType } = getReference(homeId, residentId, userId, caregiverId);
    url = await backendAPIStore.getPresignedUrlForDownload(
      referenceId,
      referenceType,
      doc.documentId,
      doc.s3Key,
      doc.documentTitle
    );
    if (url) {

      // get doc extension
      const fileExtension = doc.documentTitle?.split('.').pop()?.toLowerCase();
      if (fileExtension && IMAGE_TYPE_EXTENSIONS.includes(fileExtension.toLowerCase())) {
        setDocViewerImageUrl(url);
      } else {
        const googleDocsViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
        setDocViewerNonImageUrl(googleDocsViewerUrl);
      }
      setShowDocViewer(true);
    }
  };


  const fileHeaderTemplate = (options) => {
    const { className, chooseButton, uploadButton, cancelButton } = options;
    return (
      <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
        {chooseButton}
        {uploadButton}
        {cancelButton}
      </div>
    );
  };

  const onTemplateRemove = (file, callback) => {
    callback();
  };

  const chooseOptions = { icon: 'pi pi-fw pi-images', className: 'custom-choose-btn p-button-rounded p-button-outlined' };
  const uploadOptions = { icon: 'pi pi-fw pi-cloud-upload', className: 'custom-upload-btn p-button-success p-button-rounded p-button-outlined' };
  const cancelOptions = { icon: 'pi pi-fw pi-times', className: 'custom-cancel-btn p-button-danger p-button-rounded p-button-outlined' };

  const itemTemplate = (file, props) => {
    return (
      <div className="flex align-items-center flex-wrap border-round m-1" style={{ backgroundColor: 'rgb(250 250 250)', width: '100%' }}>
        <div className="flex align-items-center">
          <div className="flex flex-column text-left" style={{ overflowWrap: 'anywhere' }}>
            {file.name}
          </div>
        </div>
        <Tag value={props.formatSize} severity="warning" className="m-1" />
        <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={() => onTemplateRemove(file, props.onRemove)} />
      </div >
    );
  }

  const titleTemplate = (title, toolTipMessage) => {
    return (
      <div className="flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="text-xl text-900" style={{ textTransform: "capitalize" }}>{title}</span>

        {toolTipMessage && (
          <i className="pi pi-info-circle text-2xl text-warning cursor-pointer" onClick={() => {
            setHelpTextMessage(toolTipMessage); setShowHelpTextDialog(true);
          }} />
        )}
      </div>
    );
  };

  const docTableRowTemplate = (rowData: Document) => {
    return (
      <div>
        <div className="flex grid">
          <div className="col flex-column">
            <div>{rowData.documentTitle}</div>
            <div><Tag value={rowData.folderName} severity="info" className="m-1" /></div>
          </div>
          <div className="col flex-column">
            <i className="pi pi-eye m-2" style={{ cursor: "pointer", color: "blue" }} onClick={() => previewDocument(rowData)} title="Preview" data-telemetry-action="document_preview"></i>
            <i className="pi pi-download m-2" style={{ cursor: "pointer", color: "green" }} onClick={() => handleDownload(rowData)} title="Download" data-telemetry-action="document_download"></i>
            <i className="pi pi-trash m-2" style={{ cursor: "pointer", color: "red" }} onClick={() => handleDelete(rowData)} title="Delete" data-telemetry-action="document_delete"></i>
          </div>
          <div className="col flex-column">
            {convertIsoToDateOnly(rowData.createdAt)}
          </div>
        </div>
      </div>
    );
  }

  const folderFilterTemplate = (options) => {
    return <Dropdown value={options.value} options={folderOptions} onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="Select folder" className="p-column-filter" showClear style={{ width: mobile ? "100%" : "25%" }} />;
  };

  const permissionHelpText = (
    <>
      <p>Folders help organize documents for easier access and management. Each folder can represent a category or type of document, allowing you to quickly locate specific files.</p>
      <p>Share the folders with specific hub users to control who can view or manage the documents. Permissions to a shared folder will auto expire in <strong>7 days</strong></p>
    </>
  )

  const documentTableHeader = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <div className="text-xl" style={{ color: "cadetblue", textTransform: "capitalize" }}>{referenceType} Documents: ({allDoc ? allDoc.length : 0})</div>
      <Button icon="pi pi-plus" rounded severity="warning" label="Add Document" onClick={() => setShowAddNewDocuments(true)} data-telemetry-action="document_open_upload_dialog" />
    </div>
  );

  const displayedDocuments = selectedFolderForDocTable
    ? (allDoc || []).filter((doc) => doc.folderName === selectedFolderForDocTable)
    : (allDoc || []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component">
      <Toast ref={toast}></Toast>
      <SubscribeForUnlimitedAccessDialog
        visible={showDocumentLimitDialog}
        onHide={() => setShowDocumentLimitDialog(false)}
        title="More Document Access Needed"
        message="You have used the free document limit on your account. Subscribe to keep adding more documents."
      />
      {
        referenceType === ReferenceType.USER && (
          <Card style={appInnerCardStyle}>
            <div>
              Manage your personal documents here like your business license, certifications. You can create folders to organize your files and share them with other users as needed.
            </div>
            {
              userRoleType && userRoleType === USER_ROLE_TYPE.PROVIDER && (
                <>
                  <div>
                    <strong>Resident documents: </strong> For resident documents, go to the resident's profile page from your <a onClick={() => navigate(RouterAddress.PROVIDER_HOME)}>{providerWorkspaceLabel}</a>.
                  </div>
                  <div>
                    <strong>{homeDocumentsLabel} documents: </strong> For {homeDocumentsLabel.toLowerCase()}-related documents, go to your <a onClick={() => navigate(RouterAddress.PROVIDER_HOME)}>{providerWorkspaceLabel}</a>.
                  </div>
                </>
              )
            }
          </Card>)
      }

      <Card title={titleTemplate("Documents", "Choose a folder first, then upload, preview, download, or delete documents quickly.")} style={appSectionCardStyle}>
        <div className="grid align-items-end mb-3">
          <div className="col-12 md:col-4">
            <label htmlFor="documentFolderFilter" className="mb-2 block"><strong>View Folder</strong></label>
            <Dropdown
              id="documentFolderFilter"
              options={folderOptions}
              placeholder="Select folder"
              value={selectedFolderForDocTable}
              onChange={(e) => setSelectedFolderForDocTable(e.value)}
              showClear
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-8 flex justify-content-end">
            <Button icon="pi pi-plus" rounded severity="warning" label="Upload Documents" onClick={() => setShowAddNewDocuments(true)} />
          </div>
        </div>

        <div style={appDataTableWrapperStyle}>
          <DataTable
            header={<div className="font-semibold">{referenceType} documents ({displayedDocuments.length})</div>}
            value={displayedDocuments}
            filters={filters}
            filterDisplay="row"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage="No documents found for the selected folder."
          >
            <Column
              field="folderName"
              headerStyle={{ display: "none" }}
              filter
              filterElement={folderFilterTemplate}
              showFilterMenu={false}
              body={docTableRowTemplate}
            ></Column>
          </DataTable>
        </div>
      </Card>

      <Dialog
        header={renderSurfaceHeader("Add New Documents", "Choose a folder and upload one or more files to this record.", "pi pi-upload")}
        visible={showAddNewDocuments}
        modal
        onHide={() => setShowAddNewDocuments(false)}
        style={appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
      >
        <div style={appAccentPanelStyle} className="mb-3">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Upload guidance</div>
          <div className="mt-2" style={appMutedTextStyle}>
            Select the target folder first, then drag files in or choose them manually to upload.
          </div>
        </div>
        <div style={appFormSectionStyle}>
          <label htmlFor="folderDropdown" className="mr-2">Folder:</label>
          <Dropdown id="folderDropdown" options={folderOptions} placeholder="Select Folder" className="mb-3"
            value={selectedFolderForUpload} onChange={(e) => setSelectedFolderForUpload(e.value)}
          />
        </div>

        {
          uploadLoading && (
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          )
        }
        <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
          <FileUpload ref={fileUploadRef} name="profileImage" customUpload uploadHandler={handleFilesUpload} maxFileSize={APP_DOCUMENT_FILE_SIZE_IN_BYTES}
            headerTemplate={fileHeaderTemplate}
            itemTemplate={itemTemplate}
            chooseOptions={chooseOptions}
            uploadOptions={uploadOptions}
            cancelOptions={cancelOptions}
            emptyTemplate={<span className="m-0">Drag and drop files here to upload</span>}
            multiple />
        </div>
      </Dialog>

      <Card title={titleTemplate("Folders and Sharing", "Create folders, share folder access, and review who has access.")} style={appSectionCardStyle}>
        {permissionHelpText}

        <div style={appAccentPanelStyle} className="mb-4">
          <div className="grid align-items-center">
            <div className="col-12 lg:col-7">
              <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#9a3412" }}>Create a new folder</div>
              <div className="mt-2" style={appMutedTextStyle}>
                Start here when you want to organize documents into a new category before uploading or sharing files.
              </div>
            </div>
            <div className="col-12 lg:col-5 flex justify-content-start lg:justify-content-end">
              <Button
                label={showNewFolderInput ? "Hide Folder Creator" : "Create New Folder"}
                icon={showNewFolderInput ? "pi pi-minus" : "pi pi-plus"}
                severity="warning"
                rounded
                onClick={() => {
                  setShowNewFolderInput(!showNewFolderInput);
                  setShowShareUserSection(false);
                }}
              />
            </div>
          </div>
        </div>

        {showNewFolderInput && (
          <div style={{ ...appNeutralPanelStyle, marginBottom: "1rem" }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>New folder details</div>
            <div className="mt-2 mb-3" style={appMutedTextStyle}>
              Give the folder a short, recognizable name so it is easy to pick later when uploading documents.
            </div>
            <div className="flex flex-wrap align-items-center gap-2">
              <InputText
                placeholder="Examples: Licenses, Resident Intake, Staff Certifications"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                style={{ minWidth: mobile ? "100%" : "420px" }}
              />
              <Button label="Save Folder" icon="pi pi-check" className="p-button-outlined p-button-success" rounded onClick={onSaveNewFolder} data-telemetry-action="document_save_folder" />
              <Button label="Cancel" icon="pi pi-times" className="p-button-outlined p-button-secondary" rounded onClick={() => { setShowNewFolderInput(false); setNewFolderName(""); }} />
            </div>
          </div>
        )}

        <div className="grid align-items-end mb-3">
          <div className="col-12 md:col-4">
            <label htmlFor="folderDropdown" className="mb-2 block"><strong>Selected Folder</strong></label>
            <Dropdown
              id="folderDropdown"
              options={folderOptions}
              placeholder="Select folder"
              value={selectedFolderForShare}
              onChange={(e) => { setSelectedFolderForShare(e.value); loadSharedUsersForFolder(e.value); }}
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-8 flex flex-wrap justify-content-end gap-2">
            <Button label="Share Folder" icon="pi pi-share-alt" className="p-button-outlined p-button-warning" rounded onClick={() => { setShowShareUserSection(!showShareUserSection); setShowNewFolderInput(false); }} data-telemetry-action="document_toggle_share_folder" />
            <Button label="Delete Folder" icon="pi pi-trash" className="p-button-outlined p-button-danger" rounded onClick={onDeleteFolder} data-telemetry-action="document_delete_folder" />
          </div>
        </div>

        {showShareUserSection && (
          <div className="flex flex-wrap align-items-center gap-2 mb-3">
            <InputText placeholder="Enter user email" value={shareTargetUserEmailId} onChange={(e) => setShareTargetUserEmailId(e.target.value)} style={{ minWidth: mobile ? "100%" : "320px" }} />
            <Button label="Share" icon="pi pi-check" className="p-button-outlined p-button-success" rounded onClick={onSaveShareFolder} data-telemetry-action="document_confirm_share_folder" />
            <Button label="Cancel" icon="pi pi-times" className="p-button-outlined p-button-secondary" rounded onClick={() => { setShowShareUserSection(false); setShareTargetUserEmailId(""); }} />
          </div>
        )}

        <div style={appDataTableWrapperStyle}>
          <DataTable
            value={folderSharedUsersList || []}
            emptyMessage="This folder is not shared with anyone yet."
            showGridlines
            stripedRows
            paginator
            rows={5}
            className="p-datatable-sm"
          >
            <Column field="sharedUserId" header="Shared With"></Column>
            <Column header="Expires On" body={(rowData) => convertEpochToDateString(rowData.ttl)}></Column>
            <Column
              header="Actions"
              body={(rowData) => (
                <Button icon="pi pi-trash" className="p-button-outlined p-button-danger p-button-rounded" onClick={() => handleDeleteSharedFolder(rowData)} data-telemetry-action="document_remove_shared_access" />
              )}
              style={{ width: "8rem" }}
            ></Column>
          </DataTable>
        </div>
      </Card>

      {
        sharedFoldersList && sharedFoldersList.length > 0 && (
          <Card title={titleTemplate(`Folders shared with me: (${sharedFoldersList.length})`,
            "View folders and documents within them that have been shared with you by other users in this Hub.")} style={appSectionCardStyle} >
            <Dropdown value={selectedSharedFolder} options={sharedFoldersNameList} optionLabel="name"
              onChange={(e) => onSharedFolderFilterChange(e)}
              placeholder="Select shared folder" className="p-column-filter mb-3" showClear style={{ width: mobile ? "100%" : "25%" }} />

            <div className="text-sm mt-1 col flex-column">
              Folder shared by userId: <strong>{sharedFolderOwner}</strong>
              <div>Expiry Date:
                <i>&nbsp; {sharedFolderExpiration}</i>
              </div>
            </div>
            <div style={appDataTableWrapperStyle}>
              <DataTable
                value={sharedFolderDocuments || []}
                emptyMessage="No documents found in this shared folder."
                showGridlines
                stripedRows
                paginator
                rows={5}
                className="p-datatable-sm"
              >
                <Column field="documentTitle" header="Document"></Column>
                <Column field="folderName" header="Folder"></Column>
                <Column header="Added" body={(rowData) => convertIsoToDateOnly(rowData.createdAt)}></Column>
                <Column
                  header="Actions"
                  body={(rowData) => (
                    <div className="flex gap-2">
                      <Button icon="pi pi-eye" className="p-button-outlined p-button-info p-button-rounded" onClick={() => previewDocument(rowData)} data-telemetry-action="document_preview_shared" />
                      <Button icon="pi pi-download" className="p-button-outlined p-button-success p-button-rounded" onClick={() => handleDownload(rowData)} data-telemetry-action="document_download_shared" />
                    </div>
                  )}
                ></Column>
              </DataTable>
            </div>
          </Card>
        )
      }

      <Dialog
        header={renderSurfaceHeader("Summary", "Quick detail preview for the selected item.", "pi pi-info-circle")}
        visible={showHelpTextDialog}
        modal
        onHide={() => setShowHelpTextDialog(false)}
        style={appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
      >
        <div style={appFormSectionStyle}>{helpTextMessage}</div>
      </Dialog>

      <Dialog header={renderSurfaceHeader("Document Viewer", "Preview documents directly without leaving the page.", "pi pi-eye")} visible={showDocViewer} modal
        onHide={() => { setShowDocViewer(false); setDocViewerImageUrl(""); setDocViewerNonImageUrl(""); }}
        style={appWideDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}>
        {docViewerNonImageUrl &&
          <div style={appFormSectionStyle}>
            <iframe
              src={docViewerNonImageUrl}
              style={{ width: '100%', height: '500px', objectFit: 'contain', border: "1px solid #219d51", }}
              title="Document Preview Viewer"
            ></iframe>
          </div>
        }
        {
          docViewerImageUrl && (
            <div style={appFormSectionStyle}>
              <img
                src={docViewerImageUrl}
                style={{ width: '100%', height: '500px', objectFit: 'contain', border: "1px solid #219d51", }}
                title="Document Preview Viewer"
              />
            </div>
          )
        }
      </Dialog>
    </div>
  );
};

export default DocumentPage;


